import { useCallback } from 'react';

import { ArrowClockwise, ArrowCounterClockwise } from '@phosphor-icons/react/dist/ssr';

import { cn } from '@/utils';

import { formatAudioPlayerTime } from '../utils/formatAudioPlayerTime';
import { useAudioPlayer } from '../utils/useAudioPlayer';

import * as styles from './AudioPlayer.css';
import type { PlayerControlProps } from './AudioPlayer.type';
import PlayButton from './PlayButton';
import { PlaybackSpeedControl } from './PlaybackSpeedControl';

export function ClassicPlayer({
  audioUrl,
  audioType = 'audio/mpeg',
  thumbnailUrl,
  title,
  duration: knownDuration,
  primaryColor,
  backgroundColor,
  textColor,
  trackColor,
  iconColor: iconColorProp,
  showThumbnail,
  showTitle,
  showRewind,
  showForward,
  rewindSeconds,
  forwardSeconds,
  playButtonSize,
  skipButtonSize,
  progressBarHeight,
  onPlayAttempt,
  isPlaybackLocked,
  isPreview,
  audioElementId,
  playbackStorageKey,
}: PlayerControlProps) {
  const {
    audioRef,
    isPlaying,
    isReady,
    isBuffering,
    currentTime,
    duration,
    progress,
    srcAttached,
    playbackRate,
    setPlaybackRate,
    togglePlay,
    handleRewind,
    handleForward,
    seekTo,
  } = useAudioPlayer({ rewindSeconds, forwardSeconds, knownDuration, playbackStorageKey, enablePlaybackSpeed: true });

  const handleProgressClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const ratio = (e.clientX - rect.left) / rect.width;
      seekTo(ratio);
    },
    [seekTo],
  );

  const handleProgressKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (!duration) return;
      const step = 0.02;
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        seekTo(Math.min(1, currentTime / duration + step));
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        seekTo(Math.max(0, currentTime / duration - step));
      }
    },
    [duration, currentTime, seekTo],
  );

  const iconColor = iconColorProp || textColor || primaryColor;

  return (
    <div
      className={cn(styles.playerWrapper, isPlaybackLocked && 'pointer-events-none cursor-not-allowed')}
      style={{ backgroundColor }}
    >
      {showThumbnail && thumbnailUrl && <img src={thumbnailUrl} alt="" className={styles.thumbnail} />}

      <div className={styles.playerContent}>
        {(showTitle && title) || isPreview ? (
          <div className={styles.titleRow}>
            {showTitle && title ? (
              <span className={styles.title} style={{ color: textColor || undefined }} title={title}>
                {title}
              </span>
            ) : (
              <span className={styles.title} />
            )}
            {isPreview && (
              <span className={styles.previewBadge} style={{ color: textColor || undefined }}>
                Preview
              </span>
            )}
          </div>
        ) : null}

        <div className={styles.controlsRow}>
          {showRewind && (
            <button
              type="button"
              className={styles.skipButton}
              onClick={handleRewind}
              disabled={!isReady}
              aria-label={`Rewind ${rewindSeconds} seconds`}
            >
              <ArrowCounterClockwise size={skipButtonSize} weight="bold" color={iconColor} />
              <span className={styles.skipLabel} style={{ color: iconColor }}>
                {rewindSeconds}
              </span>
            </button>
          )}

          {!isPlaybackLocked && (
            <PlayButton
              isReady={isReady}
              isPlaybackLocked={isPlaybackLocked}
              isPlaying={isPlaying}
              isBuffering={isBuffering}
              playButtonSize={playButtonSize}
              iconColor={iconColor}
              togglePlay={togglePlay}
              onPlayAttempt={onPlayAttempt}
            />
          )}

          {showForward && (
            <button
              type="button"
              className={styles.skipButton}
              onClick={handleForward}
              disabled={!isReady}
              aria-label={`Forward ${forwardSeconds} seconds`}
            >
              <ArrowClockwise size={skipButtonSize} weight="bold" color={iconColor} />
              <span className={styles.skipLabel} style={{ color: iconColor }}>
                {forwardSeconds}
              </span>
            </button>
          )}

          <div className={styles.progressRow}>
            <span className={styles.timeLabel} style={{ color: textColor || undefined }}>
              {formatAudioPlayerTime(currentTime)}
            </span>

            <div
              className={styles.progressBar}
              style={{ backgroundColor: trackColor, height: progressBarHeight }}
              onClick={handleProgressClick}
              onKeyDown={handleProgressKeyDown}
              role="slider"
              aria-valuenow={currentTime}
              aria-valuemin={0}
              aria-valuemax={duration}
              aria-label="Seek audio"
              tabIndex={0}
            >
              <div
                className={styles.progressFill}
                style={{
                  width: `${progress}%`,
                  backgroundColor: primaryColor,
                }}
              />
            </div>

            <span className={styles.timeLabel} style={{ color: textColor || undefined }}>
              {formatAudioPlayerTime(duration)}
            </span>
          </div>

          <PlaybackSpeedControl value={playbackRate} onChange={setPlaybackRate} color={iconColor} />
        </div>
      </div>

      {audioUrl && (
        <audio ref={audioRef} id={audioElementId} preload={srcAttached ? 'metadata' : 'none'}>
          <source src={audioUrl} type={audioType} />
          <track kind="captions" src="" label="No captions available" />
        </audio>
      )}
    </div>
  );
}
